// Google Analytics 4 tracking utilities
// This file handles all analytics tracking for the DataCalc app

import { 
  initializeLocalAnalytics, 
  trackLocalFramingChartDownload, 
  trackLocalCalculationLog, 
  trackLocalPageView,
  getLocalAnalyticsSummary 
} from './localAnalytics';

// Set to true for local development debugging only
const DEBUG_ANALYTICS = process.env.NODE_ENV === 'development' && false;
const debugLog = (...args) => { if (DEBUG_ANALYTICS) console.log('[Analytics]', ...args); };

const CONSENT_KEY = 'datacalc_cookie_consent';

/**
 * Read stored consent. Returns 'granted', 'denied', or null.
 */
const getStoredConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
};

// EEA + UK ISO 3166-1 alpha-2 codes where GDPR requires opt-in consent.
const GDPR_REGIONS = [
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR',
  'DE','GR','HU','IE','IT','LV','LT','LU','MT','NL',
  'PL','PT','RO','SK','SI','ES','SE',
  'IS','LI','NO',  // EEA non-EU
  'GB',             // UK
];

/**
 * Initialize GA4 with region-aware Consent Mode v2.
 *
 * Strategy:
 *  - EEA / UK visitors → analytics_storage defaults to 'denied' (GDPR opt-in).
 *    GA4 still sends cookieless pings for basic measurement / behavioural
 *    modelling even while denied (Advanced Consent Mode).
 *  - All other visitors → analytics_storage defaults to 'granted' (opt-out
 *    model, compliant with US / CCPA). If the user previously opted out,
 *    their stored preference is honoured immediately.
 *
 * The gtag `region` parameter lets Google's tag infrastructure resolve the
 * visitor's region server-side — no client-side geolocation API needed.
 */
// react-snap prerenders with this user agent — analytics must be inert during
// the build, or the GA script tag gets baked into the static HTML, every build
// sends phantom pageviews, and localAnalytics throws on its uninitialized
// storage (which unmounts the React tree mid-snap).
export const isPrerendering = () =>
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

export const initializeAnalytics = () => {
  if (isPrerendering()) {
    return;
  }

  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;

    const stored = getStoredConsent();

    // --- Region-specific: EEA + UK → denied by default (GDPR opt-in) ---
    gtag('consent', 'default', {
      analytics_storage: stored || 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
      region: GDPR_REGIONS,
    });

    // --- Global fallback: everywhere else → granted by default (opt-out) ---
    gtag('consent', 'default', {
      analytics_storage: stored === 'denied' ? 'denied' : 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      enhanced_measurement: true,
      file_downloads: true,
      outbound_clicks: true,
    });

    debugLog('Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  } else {
    debugLog('Google Analytics not initialized - missing measurement ID or not in browser');
  }

  initializeLocalAnalytics();
};

/**
 * Update GA4 consent state at runtime (called from the cookie consent banner).
 * @param {'granted'|'denied'} consent
 */
export const updateAnalyticsConsent = (consent) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent,
    });
    debugLog('Analytics consent updated to:', consent);
  }
};

// Track framing chart downloads with enhanced internal data
export const trackFramingChartDownload = (chartData) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const eventData = {
      event_category: 'Framing Chart',
      event_label: `${chartData.brand} ${chartData.camera}`,
      custom_parameters: {
        camera_brand: chartData.brand,
        camera_model: chartData.camera,
        resolution: chartData.resolution,
        aspect_ratio: chartData.aspectRatio,
        safety_margin: chartData.safetyMargin,
        lens_squeeze: chartData.lensSqueeze,
        is_anamorphic: parseFloat(chartData.lensSqueeze) !== 1,
        production_name: chartData.productionName || 'Untitled',
        dop_name: chartData.dopName || 'Unknown',
        // Enhanced internal tracking
        session_id: getSessionId(),
        download_format: 'PNG', // Could be dynamic if you add other formats
        chart_size: `${chartData.width || 'unknown'}x${chartData.height || 'unknown'}`,
        user_agent: navigator.userAgent.substring(0, 100),
        referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString(),
        time_on_page: performance.now(), // Time since page load
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`
      }
    };

    // Track the download event
    window.gtag('event', 'framing_chart_download', eventData);
    
    // Also track as a conversion for business metrics
    window.gtag('event', 'conversion', {
      event_category: 'Framing Chart',
      event_label: 'Download',
      value: 1
    });

    debugLog('Tracked framing chart download:', eventData);
  }
  
  // Always track locally as backup
  trackLocalFramingChartDownload(chartData);
};

// Track calculation logs with enhanced internal data
export const trackCalculationLog = (calculatorType, logData) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const eventData = {
      event_category: 'Calculator Log',
      event_label: calculatorType,
      custom_parameters: {
        calculator_type: calculatorType,
        log_content: logData.substring(0, 100), // First 100 chars for privacy
        log_length: logData.length,
        // Enhanced internal tracking
        session_id: getSessionId(),
        user_agent: navigator.userAgent.substring(0, 100),
        timestamp: new Date().toISOString(),
        page_path: window.location.pathname,
        time_on_page: performance.now(),
        log_word_count: logData.split(/\s+/).length,
        log_line_count: logData.split('\n').length
      }
    };

    // Add specific parameters based on calculator type
    if (calculatorType.includes('Camera') || calculatorType.includes('Duration')) {
      // Extract common video parameters if possible
      const resolutionMatch = logData.match(/(\d+\s*x\s*\d+)/);
      const framerateMatch = logData.match(/(\d+(?:\.\d+)?)\s*fps/);
      const codecMatch = logData.match(/(ProRes|ARRIRAW|H\.264|H\.265|HEVC|DNxHD|RAW)/i);
      const bitrateMatch = logData.match(/(\d+(?:\.\d+)?)\s*(Mbps|Gbps|kbps)/i);
      
      if (resolutionMatch) eventData.custom_parameters.resolution = resolutionMatch[1];
      if (framerateMatch) eventData.custom_parameters.framerate = framerateMatch[1];
      if (codecMatch) eventData.custom_parameters.codec = codecMatch[1];
      if (bitrateMatch) eventData.custom_parameters.bitrate = `${bitrateMatch[1]} ${bitrateMatch[2]}`;
    }

    // Track the log event
    window.gtag('event', 'calculation_logged', eventData);

    debugLog('Tracked calculation log:', eventData);
  }
  
  // Always track locally as backup
  trackLocalCalculationLog(calculatorType, logData);
};

// Track general calculator usage with enhanced parameters
export const trackCalculatorUsage = (calculatorType, action = 'calculate', additionalData = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'calculator_usage', {
      event_category: 'Calculator',
      event_label: calculatorType,
      custom_parameters: {
        calculator_type: calculatorType,
        action: action,
        session_id: getSessionId(),
        user_agent: navigator.userAgent.substring(0, 100),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    });

    debugLog('Tracked calculator usage:', calculatorType, action);
  }
};

// Track share events
export const trackShare = (contentType, method = 'unknown') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'share', {
      event_category: 'Share',
      event_label: contentType,
      custom_parameters: {
        content_type: contentType,
        method: method
      }
    });

    debugLog('Tracked share:', contentType, method);
  }
};

// Track page views (for SPA navigation)
// Uses the modern 'event' approach instead of re-calling 'config' on every navigation,
// which avoids re-initialising the tracker and plays nicely with consent mode.
export const trackPageView = (pagePath, pageTitle) => {
  if (isPrerendering()) {
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.origin + pagePath,
    });

    debugLog('Tracked page view:', pagePath, pageTitle);
  }
  
  // Always track locally as backup
  trackLocalPageView(pagePath, pageTitle);
};

// Track custom events
export const trackCustomEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
    debugLog('Tracked custom event:', eventName, parameters);
  }
};

// Get analytics data (for dashboard - requires Analytics Reporting API)
export const getAnalyticsData = async (startDate, endDate) => {
  // Try to get data from local analytics first
  const localData = getLocalAnalyticsSummary(7); // Last 7 days
  
  if (localData) {
    return localData;
  }
  
  // This would require setting up the Analytics Reporting API for GA4 data
  // For now, return empty data structure
  return {
    framingChartDownloads: {
      total: 0,
      byCamera: {},
      byDate: {}
    },
    calculationLogs: {
      total: 0,
      byCalculator: {},
      byDate: {}
    },
    topCameras: [],
    topCalculators: []
  };
};

// Generate or get session ID for tracking user sessions
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('datacalc_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('datacalc_session_id', sessionId);
  }
  return sessionId;
};

// Track user interactions for internal analysis
export const trackUserInteraction = (element, action, details = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'user_interaction', {
      event_category: 'UI Interaction',
      event_label: `${element}_${action}`,
      custom_parameters: {
        element_type: element,
        action: action,
        session_id: getSessionId(),
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...details
      }
    });
  }
};

// Track errors for debugging and improvement
export const trackError = (errorType, errorMessage, context = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'error_occurred', {
      event_category: 'Error',
      event_label: errorType,
      custom_parameters: {
        error_type: errorType,
        error_message: errorMessage.substring(0, 150),
        session_id: getSessionId(),
        page_path: window.location.pathname,
        user_agent: navigator.userAgent.substring(0, 100),
        timestamp: new Date().toISOString(),
        ...context
      }
    });
  }
};

// Track feature usage patterns
export const trackFeatureUsage = (feature, usage_pattern, metadata = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'feature_usage', {
      event_category: 'Feature',
      event_label: feature,
      custom_parameters: {
        feature_name: feature,
        usage_pattern: usage_pattern,
        session_id: getSessionId(),
        timestamp: new Date().toISOString(),
        ...metadata
      }
    });
  }
};

// Track performance metrics for optimization
export const trackPerformance = (metric_name, value, unit = 'ms') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'performance_metric', {
      event_category: 'Performance',
      event_label: metric_name,
      value: value,
      custom_parameters: {
        metric_name: metric_name,
        metric_value: value,
        unit: unit,
        session_id: getSessionId(),
        timestamp: new Date().toISOString()
      }
    });
  }
};

// Utility to check if analytics is available
export const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' && window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID;
}; 